import { throttle } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { MediaName, ScrollPosition, WindowContext } from './context';

const getCurrentSizeName = () => {
    const widthPx = window.innerWidth;

    if (widthPx < 768) {
        return 'mobile';
    }

    if (widthPx < 992) {
        return 'tablet';
    }

    return 'desktop';
};

const getCurrentPositionName = () => {
    const currentPos = window.pageYOffset;

    if (currentPos === 0) {
        return 'top';
    }

    return 'scrolled-top';
};

export const WindowProvider: React.FC = ({ children }) => {
    const [currentMediaName, setMediaName] = useState<MediaName>(
        getCurrentSizeName(),
    );
    const [scrollPosition, setScrollPosition] = useState<ScrollPosition>(
        getCurrentPositionName(),
    );

    useEffect(() => {
        const handleResize = () => {
            const mediaName = getCurrentSizeName();
            if (currentMediaName !== mediaName) {
                setMediaName(mediaName);
            }
        };

        window.addEventListener('resize', throttle(handleResize, 300));
        return () => {
            window.removeEventListener('resize', throttle(handleResize, 300));
        };
    });

    useEffect(() => {
        const handleScroll = () => {
            const scrollName = getCurrentPositionName();
            if (scrollPosition !== scrollName) {
                setScrollPosition(scrollName);
            }
        };

        window.addEventListener('scroll', throttle(handleScroll, 300), {
            passive: false,
        });
        return () => {
            window.removeEventListener('scroll', throttle(handleScroll, 300));
        };
    });

    /**
     * Returns true whenever `currentMediaName` is `mobile` or `tablet`
     */
    const isMobile = useCallback(
        () => currentMediaName === 'mobile' || currentMediaName === 'tablet',
        [currentMediaName],
    );

    return (
        <WindowContext.Provider
            value={{ currentMediaName, isMobile, scrollPosition }}
        >
            {children}
        </WindowContext.Provider>
    );
};

export * from './context';
